<template>
    <aside
        :class="[
            'fixed top-0 left-0 z-40 h-screen',
            'transition-all small_desktop:translate-x-0',
            [hasEmailConfirmation ? 'pt-14' : 'pt-[8rem] small_desktop:pt-24'],
            { 'w-64': !navbarCollapse },
            { 'w-20': navbarCollapse },
            { '-translate-x-full': !navbarMobileOpen },
            'bg-gray-200 border-gray-300 border-r',
        ]"
        aria-label="Menu Navegação"
    >
        <div class="relative h-full w-full pt-4 overflow-x-hidden overflow-y-auto flex flex-col">
            <!-- Nav  -->
            <div class="w-full px-4">
                <nav>
                    <ul class="flex flex-col gap-1">
                        <YpSidebarItem
                            v-for="(item, index) in itensNav"
                            :key="index"
                            :item="item"
                            :ref="item.route"
                            @click="closeMobileNav"
                        />
                    </ul>
                </nav>
            </div>

            <div
                v-if="showFinancialNav"
                class="w-full px-4 mt-8"
            >
                <nav>
                    <ul class="flex flex-col gap-1">
                        <li :class="['text-sm text-gray-600 font-normal mb-2 pl-4', { hidden: navbarCollapse }]">Financeiro</li>
                        <YpSidebarItem
                            v-for="(item, index) in itensNavFinancial"
                            :key="index"
                            :item="item"
                            :ref="item.route"
                            @click="closeMobileNav"
                        />
                    </ul>
                </nav>
            </div>

            <!-- Fixed footer -->
            <div class="absolute bottom-0 left-0 right-0 py-2 px-4">
                <button
                    class="flex items-center gap-2 w-full py-1 px-2 transition-all rounded hover:bg-neutral-high_light text-gray-700 small_desktop:hidden"
                    @click="logout"
                >
                    <BaseIcon
                        class="w-6 h-6"
                        icon-name="icon-login"
                        icon-color="#56545E"
                    >
                        <IconLogin />
                    </BaseIcon>

                    <span class="flex-1 text-sm text-left font-semibold truncate"> Sair </span>
                </button>

                <button
                    :class="['items-center gap-2 w-full py-2 px-2 delay-150 rounded hover:bg-neutral-high_light text-gray-700 hidden small_desktop:flex']"
                    @click="toggleNavbarCollapse(!navbarCollapse)"
                >
                    <div :class="['flex justify-center', { 'flex-1': navbarCollapse }]">
                        <BaseIcon
                            class="w-6 h-6"
                            :icon-name="navbarCollapse ? 'icon-open-menu' : 'icon-close-menu'"
                            icon-color="#56545E"
                        >
                            <component :is="navbarCollapse ? 'icon-open-menu' : 'icon-close-menu'" />
                        </BaseIcon>
                    </div>

                    <span
                        v-if="!navbarCollapse"
                        class="flex-1 text-sm/6 text-left font-semibold truncate"
                    >
                        Recolher menu
                    </span>
                </button>
            </div>
        </div>
    </aside>
    <a
        v-if="navbarMobileOpen"
        href="javascript:"
        class="st-pusher st-pusher-nav bg-glass-low_dark transition"
        @click="closeMobileNav"
    ></a>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PROFILE_TYPES } from '@/utils/enums';
import auth from '@/mixins/auth';

// Icons
import BaseIcon from '@/icons/BaseIcon';
import IconHome from '@/icons/IconHome';
import IconDashboard from '@/icons/IconDashboard';
import IconMoney from '@/icons/IconMoney';
import IconBankStatement from '@/icons/IconBankStatement';
import IconLogin from '@/icons/IconLogin';
import IconCloseMenu from '@/icons/IconCloseMenu';
import IconOpenMenu from '@/icons/IconOpenMenu';
import YpSidebarItem from '@/components/layout/sidebar/YpSidebarItem';

export default {
    name: 'YpSidebar',

    components: {
        BaseIcon,
        IconHome,
        IconDashboard,
        IconMoney,
        IconBankStatement,
        IconLogin,
        IconCloseMenu,
        IconOpenMenu,
        YpSidebarItem,
    },

    mixins: [auth],

    data: () => ({
        itensNavFinancial: [
            {
                icon: 'icon-money',
                route: 'commission',
                label: 'Comissão',
                tag: 'router-link',
            },
            {
                icon: 'icon-work-bag',
                route: 'bank-account',
                label: 'Conta Bancária',
                tag: 'router-link',
            },
        ],
    }),

    mounted() {
        window.addEventListener('resize', this.onResize);
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },

    methods: {
        ...mapActions({
            toggleMobileMenu: 'navbar/toggleMobileMenu',
        }),

        toggleNavbarCollapse(value) {
            this.$store.dispatch('navbar/saveMode', value);
        },

        closeMobileNav() {
            const html = document.querySelector('html');
            html.classList.remove('slide-nav-active');
            this.toggleMobileMenu();
        },

        onResize(event) {
            if (this.navbarCollapse && event.target.innerWidth <= 960) {
                this.toggleNavbarCollapse(false);
            }
        },

        setTopPosition({ routeName }) {
            const [itemRef] = this.$refs[routeName];
            const headerHeight = document.querySelector('#header').offsetHeight;
            const menuItem = document.querySelector(`.menu__item-${routeName}`);

            const topPosition = itemRef.offsetTop + headerHeight;

            menuItem.style.top = `${topPosition}px`;
        },
    },

    computed: {
        ...mapGetters({
            navbarMobileOpen: 'navbar/navbarMobileOpen',
            userRoles: 'profile/userRoles',
            hasEmailConfirmation: 'auth/hasEmailConfirmation',
        }),

        navbarCollapse() {
            return this.$store.getters['auth/user'] ? this.$store.getters['navbar/navbarCollapse'] : false;
        },

        itensNav() {
            let items = [
                {
                    icon: 'icon-home',
                    route: 'home',
                    label: 'Página Inicial',
                    tag: 'router-link',
                },
            ];

            if (this.userRoles.includes(PROFILE_TYPES.SPECIALIST)) {
                items.push({
                    icon: 'icon-banner',
                    label: 'Especialista',
                    childrens: [
                        {
                            route: 'specialist.home',
                            label: 'Ferramentas',
                        },
                        {
                            route: 'specialist.dash',
                            label: 'Visão geral',
                        },
                    ],
                });
            }

            if (!this.userRoles.includes(PROFILE_TYPES.SPECIALIST)) {
                items.push({
                    icon: 'icon-banner',
                    route: 'specialist.home',
                    label: 'Especialista',
                    tag: 'router-link',
                });
            }

            if (this.userRoles.includes(PROFILE_TYPES.AFFILIATE)) {
                items.push({
                    icon: 'icon-marketing',
                    label: 'Afiliado',
                    childrens: [
                        {
                            route: 'affiliate.home',
                            label: 'Ferramentas',
                        },
                        {
                            route: 'affiliate.dash',
                            label: 'Visão geral',
                        },
                    ],
                });
            }

            if (!this.userRoles.includes(PROFILE_TYPES.AFFILIATE)) {
                items.push({
                    icon: 'icon-marketing',
                    route: 'affiliate.home',
                    label: 'Afiliado',
                    tag: 'router-link',
                });
            }

            if (!this.userRoles.includes(PROFILE_TYPES.DEVELOPER)) {
                items.push({
                    icon: 'icon-code-editor',
                    route: 'developer.home',
                    label: 'Parceiro Tech',
                    tag: 'router-link',
                });
            }

            if (this.userRoles.includes(PROFILE_TYPES.DEVELOPER)) {
                items.push({
                    icon: 'icon-code-editor',
                    label: 'Parceiro Tech',
                    groupName: 'developer',
                    childrens: [
                        {
                            route: 'developer.home',
                            label: 'Inicio',
                        },
                        {
                            route: 'developer.apps',
                            label: 'Aplicativos',
                        },
                        {
                            route: 'developer.partner-environment',
                            label: 'Lojas teste',
                        },
                    ],
                });
            }

            return items;
        },

        showFinancialNav() {
            return this.userRoles.includes(PROFILE_TYPES.AFFILIATE) || this.userRoles.includes(PROFILE_TYPES.SPECIALIST);
        },
    },
};
</script>

<style lang="scss" scoped>
.sidebar {
    &.collapse {
        visibility: visible;
        width: 72px;
    }
}

.sidebar .menu__item svg {
    flex-shrink: 0;
}

.sidebar .menu .menu__item {
    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 4px;
        border-radius: 0 50px 50px 0;
        background-color: transparent;
        opacity: 0;
        transition: background-color 400ms;
    }

    &.active {
        background-color: theme('colors.gray.300');

        &:before {
            background-color: theme('colors.black');
            opacity: 1;
        }

        span {
            color: theme('colors.black');
        }
    }
}

@media only screen and (max-width: 959px) {
    .sidebar .menu__item {
        &:before {
            content: unset;
        }

        &.active {
            background-color: transparent;
        }
    }
}
</style>
